import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import Page from 'components/UI/Page';
import Button from 'components/UI/Button';
import { renderToast, toast } from 'components/UI/ToastNotification';
import { useAPI } from 'lib/API';
import ProductBadge from 'data/ProductBadge';
import {
  loadProductBadges,
  createProductBadge,
  deactivateProductBadge
} from 'requests/ProductBadges';
import AddBadgeForm from './AddBadgeForm';
import BadgeSection from './BadgeSection';
import DeactivateBadgeModal from './DeactivateBadgeModal';
import {
  PRODUCT_BADGES_TAB_LABEL,
  PRODUCT_BADGES_SUBTITLE,
  SAVED_BADGES_TITLE,
  DEACTIVATED_BADGES_TITLE,
  DEACTIVATE_TEXT,
  DEACTIVATED_STATUS_TEXT,
  NO_SAVED_BADGES,
  NO_DEACTIVATED_BADGES,
  BADGE_SAVE_SUCCESS,
  BADGE_SAVE_FAILURE,
  BADGE_DEACTIVATE_FAILURE,
  BADGE_LOAD_FAILURE
} from 'constants/productBadges';
import { Main, Text, Shade, Background } from 'styles/themeColors';
import { BorderRadius } from 'styles/themeBorderRadius';
import { FontSize, FontWeight } from 'lib/styles';

const PageTitle = styled.h2`
  margin: 0 0 4px 0;
  font-size: ${FontSize.large};
  font-weight: ${FontWeight.bold};
`;

const Subtitle = styled.p`
  margin: 0 0 24px 0;
  font-size: ${FontSize.semi};
  color: ${Text.secondary};
`;

const StatusChip = styled.span`
  display: inline-block;
  padding: 6px 16px;
  border-radius: ${BorderRadius.s};
  background-color: ${Background.shaded};
  color: ${Shade.shade450};
  font-size: ${FontSize.small};
`;

interface Props {
  setIsSaveDisabled: (isSaveDisabled: boolean) => void;
  setHasUnsavedChanges: (value: boolean) => void;
}

const ProductBadgesTab = forwardRef<{ handleSave: () => void }, Props>(
  ({ setIsSaveDisabled }, ref) => {
    const [badges, setBadges] = useState<ProductBadge[] | null>(null);
    const [badgeToDeactivate, setBadgeToDeactivate] = useState<ProductBadge | null>(null);
    // Remounting the form is the reset — cheaper than plumbing a ref just to clear four fields.
    const [formKey, setFormKey] = useState(0);
    const addBadgeFormRef = useRef<{ save: () => void } | null>(null);

    const badgesReq = useAPI({
      deferFn: loadProductBadges,
      onResolve: (result: ProductBadge[]) => setBadges(result || []),
      onReject: (err: Error) => {
        setBadges([]);
        renderToast(toast.TYPE.ERROR, err.message || BADGE_LOAD_FAILURE);
      }
    });

    const createReq = useAPI({
      deferFn: createProductBadge,
      onResolve: () => {
        renderToast(toast.TYPE.SUCCESS, BADGE_SAVE_SUCCESS);
        setFormKey(key => key + 1);
        badgesReqRef.current.run();
      },
      onReject: (err: Error) => renderToast(toast.TYPE.ERROR, err.message || BADGE_SAVE_FAILURE)
    });

    const deactivateReq = useAPI({
      deferFn: deactivateProductBadge,
      onResolve: () => {
        setBadgeToDeactivate(null);
        badgesReqRef.current.run();
      },
      onReject: (err: Error) =>
        renderToast(toast.TYPE.ERROR, err.message || BADGE_DEACTIVATE_FAILURE)
    });

    // Latest-ref so the mount effect closes over nothing reactive — no exhaustive-deps suppression.
    const badgesReqRef = useRef(badgesReq);
    badgesReqRef.current = badgesReq;

    useEffect(() => {
      badgesReqRef.current.run();
    }, []);

    useEffect(() => {
      setIsSaveDisabled(createReq.isLoading);
    }, [createReq.isLoading, setIsSaveDisabled]);

    const handleDeactivate = () => {
      if (!badgeToDeactivate) return;
      deactivateReq.run(badgeToDeactivate.id);
    };

    useImperativeHandle(ref, () => ({
      // Dispatched by the Settings header Save button, like every other tab.
      handleSave: () => addBadgeFormRef.current?.save()
    }));

    const activeBadges = useMemo(() => (badges || []).filter(badge => badge.isActive), [badges]);
    const inactiveBadges = useMemo(() => (badges || []).filter(badge => !badge.isActive), [badges]);

    return (
      <Page padding="0 0 24px 0" isLoading={badgesReq.isLoading && badges === null}>
        <PageTitle>{PRODUCT_BADGES_TAB_LABEL}</PageTitle>
        <Subtitle>{PRODUCT_BADGES_SUBTITLE}</Subtitle>

        <AddBadgeForm
          key={formKey}
          ref={addBadgeFormRef}
          badges={badges || []}
          isSaving={createReq.isLoading}
          onSave={(name, colorHash) => createReq.run(name, colorHash)}
        />

        <BadgeSection
          title={SAVED_BADGES_TITLE}
          badges={activeBadges}
          lastColumnHeader="Action"
          emptyMessage={NO_SAVED_BADGES}
          renderLastColumn={badge => (
            <Button
              isGhost
              padding="0 24px"
              theme="brandDark"
              color={Main.brandDark}
              borderColor={Main.brandDark}
              onClick={() => setBadgeToDeactivate(badge)}
            >
              {DEACTIVATE_TEXT}
            </Button>
          )}
        />

        <BadgeSection
          title={DEACTIVATED_BADGES_TITLE}
          badges={inactiveBadges}
          lastColumnHeader="Status"
          emptyMessage={NO_DEACTIVATED_BADGES}
          renderLastColumn={() => <StatusChip>{DEACTIVATED_STATUS_TEXT}</StatusChip>}
        />

        <DeactivateBadgeModal
          badge={badgeToDeactivate}
          isDeactivating={deactivateReq.isLoading}
          onClose={() => setBadgeToDeactivate(null)}
          onConfirm={handleDeactivate}
        />
      </Page>
    );
  }
);

export default ProductBadgesTab;